// Petites actions partagées par les règles et le contenu (journal, carnet, argent, crédits).
import type { TextKey } from '../content/fr';
import type { TextParams } from '../engine/i18n';
import { clamp, loanMonthlyPayment, round2 } from '../engine/finance';
import type { GameState, Loan, PageId } from '../engine/state';
import { onSavingsWithdrawal } from './livret';

export function addJournal(state: GameState, key: TextKey, params?: TextParams): void {
  state.journal.push({ day: state.day, key, params });
  if (state.journal.length > 300) state.journal.shift();
}

/** Message éphémère affiché en bas de l'écran. */
export function addNotice(state: GameState, key: TextKey, params?: TextParams): void {
  state.notices.push({ id: state.nextNoticeId++, key, params });
  if (state.notices.length > 4) state.notices.shift();
}

export function unlockPage(state: GameState, page: PageId): void {
  if (state.pages.includes(page)) return;
  state.pages.push(page);
  state.unreadPages.push(page);
}

/** Programme la suite d'un événement dans quelques jours, avec ses montants. */
export function scheduleFollowUp(state: GameState, id: string, days: number, params: Record<string, number>): void {
  state.followUps = [...state.followUps, { id, day: state.day + days, params }];
}

export function addWellbeing(state: GameState, delta: number): void {
  state.wellbeing = clamp(state.wellbeing + delta, 0, 100);
}

/** On devient « à plat » en passant sous ce niveau d'énergie… */
export const LOW_ENERGY_THRESHOLD = 4;
/** …et on le reste jusqu'à ce que l'énergie remonte à ce niveau. */
export const LOW_ENERGY_RECOVERED = 10;
/** Passer à plat coûte d'un coup ce bien-être. */
export const LOW_ENERGY_SHOCK = 5;

/** Le passage « à plat » : appelé après chaque baisse d'énergie. */
export function applyEnergyDrop(state: GameState, before: number): void {
  if (state.lowEnergyHit || before < LOW_ENERGY_THRESHOLD || state.energy >= LOW_ENERGY_THRESHOLD) return;
  state.lowEnergyHit = true;
  addWellbeing(state, -LOW_ENERGY_SHOCK);
  addNotice(state, 'work.lowEnergyShock');
}

/** L'énergie remontée à 10, on n'est plus à plat : le prochain passage sous 4 coûtera de nouveau. */
export function rearmLowEnergy(state: GameState): void {
  if (state.lowEnergyHit && state.energy >= LOW_ENERGY_RECOVERED) state.lowEnergyHit = false;
}

/** Une sortie, une nuit courte : l'énergie baisse, sans descendre sous zéro. */
export function loseEnergy(state: GameState, amount: number): void {
  const before = state.energy;
  state.energy = Math.max(0, state.energy - amount);
  applyEnergyDrop(state, before);
}

// Le découvert : autorisé jusqu'à 500 € si on l'a accepté au prologue, sinon aucun.
// Au-delà de la limite, c'est un découvert non autorisé : plus cher, avec des frais.
export const AUTHORIZED_OVERDRAFT = 500;
export const AUTHORIZED_OVERDRAFT_RATE = 0.16;
export const UNAUTHORIZED_OVERDRAFT_RATE = 0.22;
export const OVERDRAFT_INCIDENT_FEE = 8;
export const OVERDRAFT_FEES_MONTHLY_CAP = 80;

export function overdraftLimit(state: GameState): number {
  return state.overdraftAccepted ? AUTHORIZED_OVERDRAFT : 0;
}

/** Frais d'incident quand le compte passe au-delà du découvert autorisé (plafonnés chaque mois). */
export function chargeOverdraftFee(state: GameState, before: number): void {
  // Le plan de surendettement gèle les frais et les agios.
  if (state.surendettement) return;
  const limit = -overdraftLimit(state);
  if (before < limit || state.cash >= limit) return;
  const fee = Math.min(OVERDRAFT_INCIDENT_FEE, OVERDRAFT_FEES_MONTHLY_CAP - state.overdraftFeesThisMonth);
  if (fee <= 0) return;
  state.cash = round2(state.cash - fee);
  state.overdraftFeesThisMonth += fee;
  addNotice(state, 'bank.feeNotice', { fee: `${fee} €` });
}

/** Les agios se calculent chaque jour passé dans le rouge ; ils sont prélevés en fin de mois. */
export function accrueOverdraftInterest(state: GameState, days: number): void {
  if (state.cash >= 0 || state.surendettement) return;
  const debt = -state.cash;
  const authorized = Math.min(debt, overdraftLimit(state));
  const unauthorized = debt - authorized;
  const yearly = authorized * AUTHORIZED_OVERDRAFT_RATE + unauthorized * UNAUTHORIZED_OVERDRAFT_RATE;
  state.accruedAgios += (yearly * days) / 365;
}

export function payFromCash(state: GameState, amount: number): void {
  const before = state.cash;
  state.cash = round2(state.cash - amount);
  chargeOverdraftFee(state, before);
}

/** Paie avec les livrets : le LDDS d'abord, puis le Livret A, et le LEP (le mieux payé) en dernier. */
export function payFromLivret(state: GameState, amount: number): void {
  const fromLdds = Math.min(Math.max(0, state.ldds), amount);
  state.ldds = round2(state.ldds - fromLdds);
  const rest = amount - fromLdds;
  const fromLep = Math.min(Math.max(0, state.lep), Math.max(0, rest - Math.max(0, state.livret)));
  state.lep = round2(state.lep - fromLep);
  state.livret = round2(state.livret - (rest - fromLep));
  // Sur un livret, l'argent retiré cesse de rapporter dès le début de la quinzaine.
  onSavingsWithdrawal(state);
}

/** Paie d'abord avec le compte courant, puis avec les livrets. */
export function payFromSavings(state: GameState, amount: number): void {
  const fromCash = Math.min(Math.max(0, state.cash), amount);
  payFromCash(state, fromCash);
  payFromLivret(state, amount - fromCash);
}

export function addLoan(
  state: GameState,
  label: TextKey,
  principal: number,
  annualRate: number,
  months: number,
  deferredUntilJob = false,
): Loan {
  const monthlyPayment = round2(loanMonthlyPayment(principal, annualRate, months));
  const loan: Loan = {
    id: state.nextLoanId++,
    label,
    principal,
    balance: principal,
    annualRate,
    months,
    monthsLeft: months,
    monthlyPayment,
    totalInterest: round2(monthlyPayment * months - principal),
    deferredUntilJob,
  };
  state.loans.push(loan);
  unlockPage(state, 'cout_credit');
  return loan;
}
